(ns quuppa.qsm.planner.view
  (:require [clojure.string :as str]
            [re-frame.core :as rf]
            [shadow.lazy :as lazy]
            [reitit.frontend.easy :as rfe]
            [quuppa.peweb.query :as q]
            [quuppa.qsm.planner.schema :as s]
            [quuppa.qsm.planner.events :as e]
            [quuppa.qsm.planner.subs :as su]
            [quuppa.components :as c]
            [komponentit.input :as input]))

;; Planner:
;; A tool customer would use to plan a Quuppa site installation.
;; One site installation might contain be running multiple PE nodes!
;; So the plan doesn't map to single QuuppaProject directly.
;; Also check schema ns.

(defn plan-tree [plan-id id]
  (let [node @(rf/subscribe [::su/site-plan plan-id :nodes id])]
    [:li
     {:class [(when @(rf/subscribe [::su/node-selected? id])
                "active")]}
     [:a
      {:on-click #(rf/dispatch [:nav/update-query assoc :selected id])}
      (:type node) ": "
      (or (-> node :properties :name)
          "Unnamed node")]
     (when (seq (:children node))
       [:ul
        (for [id (:children node)]
          ^{:key id}
          [plan-tree plan-id id])])]))

(defn plan-edit [plan-id]
  (let [selected @(rf/subscribe [:nav/match :parameters :query :selected])
        coordinate-system @(rf/subscribe [::su/find-parent-by-type plan-id selected "CoordinateSystem"])]
    [:<>
     [:div.d-flex.flex-row.align-items-center.justify-content-between.mb-3
      [:h3.m-0 (or @(rf/subscribe [::su/site-plan plan-id :nodes "root" :properties :name])
                   "Site plan")]
      [:button.btn.btn-primary
       {:on-click #(rf/dispatch [::e/save-plan plan-id])}
       "Save plan"]]

     [:div.d-flex.flex-row.flex-grow-1.site-plan
      [:div.site-plan__sidebar
       [:ul.site-plan__tree
        [plan-tree plan-id "root"]]

       (when selected
         (let [{node-type :type, :as selected-node}
               @(rf/subscribe [::su/site-plan plan-id :nodes selected])

               assoc-value #(rf/dispatch [::e/assoc-property plan-id selected %1 (.. %2 -target -value)])
               assoc-float #(rf/dispatch [::e/assoc-property plan-id selected %1 (js/parseFloat (.. %2 -target -value))])]
           [:div.pt-4
            [:div.d-flex.flex-row.justify-content-between.align-content-center.mb-3.site-plan__sidebar-header
             [:h3.m-0
              (or (:name (:properties selected-node))
                  "Unnamed node")]
             [:button.btn.btn-sm
              {:on-click #(rf/dispatch [:nav/update-query dissoc :selected])}
              "×"]]

            [:div.mb-3
             (for [new-node-type (get s/available-children node-type)]
               [:button.btn.btn-outline-primary.mr-3
                {:key new-node-type
                 :on-click #(rf/dispatch [::e/add-node plan-id selected new-node-type])}
                "Add " new-node-type])
             (when-not (= "Root" node-type)
               [:button.btn.btn-warning.mr-3
                {:on-click #(rf/dispatch [::e/remove-node plan-id selected])}
                "Remove"])]

            ;; Tools:

            [:form
             [:div.form-group
              [:label "Type"]
              [:p.form-control-static node-type]]

             [:div.form-group
              [:label "Name"]
              [:input.form-control
               {:type "text"
                :value (:name (:properties selected-node))
                :on-change (partial assoc-value :name)}]]

             (case node-type
               "CoordinateSystem"
               [:<>
                [:h4 "Coordinate system properties"]]

               "Zone"
               [:<>
                [:h4 "Zone properties"]

                [:div.form-group
                 [:label "Points"]
                 [input/textarea
                  {:class "form-control"
                   :value (str/join " " (map (fn [[x y]]
                                               (str x "," y))
                                             (:points (:properties selected-node))))
                   :on-change (fn [v]
                                (let [v (->> (str/split v #" ")
                                             (mapv (fn [s]
                                                     (let [[x y] (str/split s #",")]
                                                       [(js/parseFloat x) (js/parseFloat y)]))))]
                                  (rf/dispatch [::e/assoc-property plan-id selected :points v])))}]]]

               "BackgroundImage"
               [:<>
                [:h4 "Background image properties"]

                [:div.form-group
                 [:label "Image file"]
                 (if-let [image (:image (:properties selected-node))]
                   [:p.form-control-static
                    [:img
                     {:src (str "/planner/files/" image)}]]
                   [:input.form-control
                    {:type "file"
                     :on-change (fn [e]
                                  (rf/dispatch [::e/set-image-resource plan-id selected (.. e -target -files (item 0))]))}])]

                [:div.d-flex.flex-row
                 [:div.form-group.flex-grow-1.mr-3
                  [:label "X"]
                  [:input.form-control
                   {:type "number"
                    :value (:x (:properties selected-node))
                    :on-change (partial assoc-float :x)}]]

                 [:div.form-group.flex-grow-1
                  [:label "Y"]
                  [:input.form-control
                   {:type "number"
                    :value (:y (:properties selected-node))
                    :on-change (partial assoc-float :y)}]]]

                [:div.d-flex.flex-row
                 [:div.form-group.flex-grow-1.mr-3
                  [:label "Width"]
                  [:input.form-control
                   {:type "number"
                    :value (:width (:properties selected-node))
                    :on-change (partial assoc-float :width)}]]

                 [:div.form-group.flex-grow-1
                  [:label "Height"]
                  [:input.form-control
                   {:type "number"
                    :value (:height (:properties selected-node))
                    :on-change (partial assoc-float :height)}]]]

                [:div.form-group
                 [:label "Rotation"]
                 [:input.form-control
                  {:type "number"
                   :value (:rotation (:properties selected-node))
                   :on-change (partial assoc-float :rotation)}]
                 [:p.help-text
                  "Degrees, rotated around CS origin point."]]]

               "Locator"
               [:<>
                [:h4 "Locator properties"]

                [:div.d-flex.flex-row
                 [:div.form-group.mr-3
                  [:label "X"]
                  [:input.form-control
                   {:type "number"
                    :value (:x (:properties selected-node))
                    :on-change (partial assoc-float :x)}]]

                 [:div.form-group.mr-3
                  [:label "Y"]
                  [:input.form-control
                   {:type "number"
                    :value (:y (:properties selected-node))
                    :on-change (partial assoc-float :y)}]]

                 [:div.form-group
                  [:label "Z"]
                  [:input.form-control
                   {:type "number"
                    :value (:z (:properties selected-node))
                    :on-change (partial assoc-float :z)}]]]]

               nil)]]))]
      [:div.site-plan__map
       (if coordinate-system
         [c/lazy-component
          {:fallback [:div "Loading view"]}
          (lazy/loadable quuppa.qsm.planner.map/mapview-root)
          plan-id]
         [:p "Select a coordinate system (or a children) to view the map."])]]]))

(defn plan-list []
  [:<>
   [:div.d-flex.flex-row.align-items-center.justify-content-between.mb-3
    [:h3.m-0 "Planner"]

    [:a.btn.btn-primary
     {:type "button"
      :href "#"
      :on-click (fn [e]
                  (.preventDefault e)
                  (rf/dispatch [::e/init-new-plan]))}
     "Create site plan"]]

   [:table
    [:thead
     [:tr
      [:th "Name"]
      [:th "Created"]]]
    [:tbody
     (for [plan (:result @(q/subscribe e/plans-query {}))]
       [:tr
        {:key (:id plan)
         :on-click #(rfe/push-state :planner-site-plan {:plan-id (:id plan)})}
        [:th (:name plan)]
        [:th (str (:created plan))]])]]])

(defn page []
  [:section.section.d-flex.flex-column.flex-grow-1
   [:div.d-flex.flex-row.flex-grow-1
    [:div.content.d-flex.flex-column.flex-grow-1.p-4
     [:div.d-flex.flex-column.flex-grow-1
      (case @(rf/subscribe [:nav/match :data :name])
        :planner-new-plan [plan-edit :new]
        :planner-site-plan [plan-edit @(rf/subscribe [:nav/match :parameters :path :plan-id])]
        [plan-list])]]]])

(def list-controller
  {:id :planner-list
   :subscriptions (fn [_match]
                    [[:planner/new-plan]])
   :start (fn [_params]
            (q/execute e/plans-query {}))})

(def new-plan-controller
  {:id :planner-new-plan
   :start (fn [])})

(def plan-controller
  {:id :planner-plan
   :parameters {:path [:plan-id]}
   :subscriptions (fn [{{{:keys [plan-id]} :path} :parameters}]
                    [[:planner/plan-updated {:id plan-id}]])
   :start (fn [{{:keys [plan-id]} :path}]
            (q/execute e/plan-query {:id plan-id} {:on-success [::e/plan-loaded plan-id]}))})
